import type { AppState, DayTemplate, LocalEvent, Overlay, PrepPlan, PrepRule, PrepTemplate, SourceItem } from '@autoday/core';

/**
 * Beständig lagring. Webb: IndexedDB. Native (Capacitor): SQLite. localStorage används aldrig som
 * kalenderdatabas. Varje ändring skrivs i EN transaktion – antingen allt eller inget.
 */
export interface DeviceState {
  deviceId: string;
  snoozes: import('@autoday/core').SnoozeRecord[];
  /** Larmpunkter som visats/levererats på denna enhet: id → tid. */
  delivered: Record<string, string>;
  lastPlannedAt: string | null;
  lastPlan: import('@autoday/core').NotificationPlan | null;
  notificationTest: NotificationTestRecord | null;
  /** Endast från versionen före profilerna – läses vid migrering, skrivs inte längre. */
  account: AccountLink | null;
  /** Kontoprofil: enheten är registrerad hos servern för detta konto. */
  deviceRegistered: boolean;
  /** Gästprofil: vad som redan lagts till i vilket konto (profil-ID → postnyckel → fingeravtryck). */
  guestTransfers: Record<string, { at: string; keys: Record<string, string> }>;
  syncCursor: number;
  lastSyncAt: string | null;
  lastSyncError: string | null;
  firstRunDone: boolean;
  /** One approved introductory plan per device; existing installations start unused. */
  introPlanUsed?: boolean;
  /** Local-only activation milestones; never sent with calendar sync. */
  planningIntro?: { firstPreviewAt?: string; firstApprovalAt?: string };
  observedErrors: Array<{ at: string; channel: string; message: string }>;
  /** Serverns senast kända revision per synkpost (entity:id). */
  serverRevisions: Record<string, number>;
  /** Olösta synkkonflikter – beständiga så att en omladdning aldrig leder till tyst överskrivning. */
  syncConflicts: import('../sync/syncClient.ts').SyncConflict[];
  /** Förberedelseplanernas EGEN pull-markör (prepSince). Börjar på 0 när servern får stöd. */
  prepCursor: number;
  /** Serverns stöd för förberedelseplaner (GET /api/config features.prepPlans). null = okänt. */
  serverPrep: { supported: boolean | null; checkedAt: string | null };
  /** Skickat kontogodkännande vars svar inte bekräftats – kontrolleras med samma opId (resolve). */
  prepPendingCommit: (import('../prep/commit.ts').PendingPlanCommit & { request: import('@autoday/core').PlanCommitRequest }) | null;
  /**
   * Ökas när en server avvisat planposter (BAD_ENTITY). Servern sparar avvisningen per opId, så nästa
   * försök med samma poster måste ha nya opId:n – annars upprepas avvisningen även efter en uppgradering.
   */
  prepOpEpoch: number;
}

export interface AccountLink {
  serverUrl: string;
  email: string;
  /** Native: bärartoken i plattformens säkra lagring; webb: httpOnly-cookie (inget token här). */
  tokenRef: 'cookie' | 'secure-storage';
  deviceRegistered: boolean;
}

export interface NotificationTestRecord {
  id: string;
  channel: string;
  scheduledAt: string;
  fireAt: string;
  /** 'scheduled' = transporten accepterade; 'confirmed' = användaren såg notisen; 'not-seen' = användaren såg den inte. */
  result: 'scheduled' | 'confirmed' | 'not-seen' | 'failed';
  detail?: string;
  /** Behörigheten när testet schemalades – ett test gäller inte efter att behörigheten ändrats. */
  permissionAtTest?: string;
  /** När användaren svarade. */
  confirmedAt?: string;
}

export interface OutboxOp {
  seq?: number;
  opId: string;
  createdAt: string;
  entity: import('../sync/protocol.ts').SyncEntity;
  id: string;
  baseRevision: number | null;
  doc: unknown;
  deleted?: boolean;
  importId?: string;
  /**
   * 'prepImport' = gästöverförd förberedelseplan: doc = { plan, events } skickas som EN atomisk import
   * (POST /api/prep/commit) – aldrig som vanlig post och aldrig sammanslagen med andra operationer.
   */
  kind?: 'prepImport';
}

/**
 * Någon annan (en annan flik med samma profil) har skrivit sedan den här instansen senast läste.
 * Ingenting har skrivits: läs om och räkna om ändringen på aktuella data.
 */
export class StaleStateError extends Error {
  constructor() {
    super('Datan har ändrats i en annan flik – läses om.');
    this.name = 'StaleStateError';
  }
}

export interface StorageAdapter {
  readonly kind: 'indexeddb' | 'sqlite' | 'memory';
  load(): Promise<AppState | null>;
  /**
   * Skriver skillnaden prev → next atomiskt, plus ev. utkorgsoperationer i samma transaktion.
   * Kastar StaleStateError (utan att skriva) om en annan flik skrivit sedan senaste load/persist.
   */
  persist(prev: AppState | null, next: AppState, outbox?: OutboxOp[]): Promise<void>;
  loadDevice(): Promise<DeviceState | null>;
  /** Kastar StaleStateError (utan att skriva) om en annan flik skrivit sedan senaste loadDevice/saveDevice. */
  saveDevice(d: DeviceState): Promise<void>;
  /** Valfritt: meddelas när en annan flik har skrivit (webb). Returnerar avregistrering. */
  onExternalChange?(cb: (what: 'state' | 'device') => void): () => void;
  outbox(): Promise<OutboxOp[]>;
  removeOutbox(seqs: number[]): Promise<void>;
  /** Tömmer profilens lokala data. */
  wipe(): Promise<void>;
  /** Raderar profilens hela lokala databas (endast efter uttryckligt val). */
  destroy(): Promise<void>;
}

export type MetaRecord = Pick<AppState, 'schemaVersion' | 'settings' | 'calendars' | 'categories' | 'sources' | 'appliedOps'>;

export function metaOf(s: AppState): MetaRecord {
  return { schemaVersion: s.schemaVersion, settings: s.settings, calendars: s.calendars, categories: s.categories, sources: s.sources, appliedOps: pruneOps(s.appliedOps) };
}

/** Behåll operations-ID:n i 90 dagar (skydd mot sena omförsök) – men inte för evigt. */
function pruneOps(ops: Record<string, string>): Record<string, string> {
  const keys = Object.keys(ops);
  if (keys.length < 5000) return ops;
  const cutoff = new Date(Date.now() - 90 * 864e5).toISOString();
  return Object.fromEntries(Object.entries(ops).filter(([, t]) => t >= cutoff));
}

export interface Diff {
  meta: boolean;
  events: { put: LocalEvent[]; del: string[] };
  sourceItems: { put: SourceItem[]; del: string[] };
  overlays: { put: Overlay[]; del: string[] };
  templates: { put: DayTemplate[]; del: string[] };
  prepTemplates: { put: PrepTemplate[]; del: string[] };
  prepRules: { put: PrepRule[]; del: string[] };
  prepPlans: { put: PrepPlan[]; del: string[] };
}

/** Diff via objektidentitet – domänen uppdaterar alltid oföränderligt. */
export function diffState(prev: AppState | null, next: AppState): Diff {
  const d = <T>(a: Record<string, T> | undefined, b: Record<string, T>) => {
    const put: T[] = [];
    const del: string[] = [];
    for (const [k, v] of Object.entries(b)) if (!a || a[k] !== v) put.push(v);
    if (a) for (const k of Object.keys(a)) if (!(k in b)) del.push(k);
    return { put, del };
  };
  return {
    meta:
      !prev ||
      prev.settings !== next.settings ||
      prev.calendars !== next.calendars ||
      prev.categories !== next.categories ||
      prev.sources !== next.sources ||
      prev.appliedOps !== next.appliedOps ||
      prev.schemaVersion !== next.schemaVersion,
    events: d(prev?.events, next.events),
    sourceItems: d(prev?.sourceItems, next.sourceItems),
    overlays: d(prev?.overlays, next.overlays),
    templates: d(prev?.templates, next.templates),
    prepTemplates: d(prev?.prepTemplates, next.prepTemplates ?? {}),
    prepRules: d(prev?.prepRules, next.prepRules ?? {}),
    prepPlans: d(prev?.prepPlans, next.prepPlans ?? {}),
  };
}

export function emptyDevice(deviceId: string): DeviceState {
  return {
    deviceId,
    snoozes: [],
    delivered: {},
    lastPlannedAt: null,
    lastPlan: null,
    notificationTest: null,
    account: null,
    syncCursor: 0,
    lastSyncAt: null,
    lastSyncError: null,
    firstRunDone: false,
    introPlanUsed: false,
    observedErrors: [],
    serverRevisions: {},
    syncConflicts: [],
    deviceRegistered: false,
    guestTransfers: {},
    prepCursor: 0,
    serverPrep: { supported: null, checkedAt: null },
    prepPendingCommit: null,
    prepOpEpoch: 0,
  };
}
